import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import Dropdown from "../commons/Dropdown"
import { getAllCategories } from "../../firebase/categories"
import {
    applyShopifyCategoryMappings,
    getShopifyCategoryMappings,
    saveShopifyCategoryMappings
} from "../../firebase/shopify"

/**
 * One row per Shopify category -- every category found on the seller's
 * listings, plus any rule saved for a category none of them currently use, so
 * saving never silently drops a rule the seller set up earlier.
 */
const buildRows = (shopifyCategories, mappings) => {
    const rows = new Map()

    for (const shopifyCategory of shopifyCategories) {
        rows.set(shopifyCategory.id, {
            shopify_category_id: shopifyCategory.id,
            name: shopifyCategory.name,
            full_name: shopifyCategory.full_name,
            count: shopifyCategory.count,
            category: "",
            sub_category: ""
        })
    }

    for (const mapping of mappings) {
        const row = rows.get(mapping.shopify_category_id) || {
            shopify_category_id: mapping.shopify_category_id,
            name: (mapping.shopify_category_name || "").split(" > ").pop(),
            full_name: mapping.shopify_category_name || "",
            count: 0
        }
        rows.set(mapping.shopify_category_id, { ...row, category: mapping.category, sub_category: mapping.sub_category || "" })
    }

    return [...rows.values()]
}

const toMappings = (rows) => rows
    .filter((row) => row.category)
    .map((row) => ({
        shopify_category_id: row.shopify_category_id,
        shopify_category_name: row.full_name,
        category: row.category,
        sub_category: row.sub_category
    }))

const describeResult = ({ updated = 0, kept_existing: keptExisting = 0 }) => {
    const parts = [`${updated} listing${updated === 1 ? "" : "s"} updated`]
    if (keptExisting > 0) {
        parts.push(`${keptExisting} already categorized listing${keptExisting === 1 ? "" : "s"} left unchanged`)
    }
    return parts.join(", ") + "."
}

export default function DialogShopifyCategoryMapping({ open, onClose, onSaved }) {
    const [categories, setCategories] = useState([])
    const [rows, setRows] = useState([])
    const [savedMappings, setSavedMappings] = useState("[]")
    const [withoutCategory, setWithoutCategory] = useState(0)
    const [loading, setLoading] = useState(true)
    // The action currently running, for the busy label and disabled state.
    const [runningAction, setRunningAction] = useState(null)
    const busy = runningAction !== null
    // 'save' or 'apply' while asking whether to change categorized listings.
    const [pendingAction, setPendingAction] = useState(null)

    useEffect(() => {
        if (!open) return

        setLoading(true)
        setPendingAction(null)
        Promise.all([getAllCategories(), getShopifyCategoryMappings()])
            .then(([cats, data]) => {
                const loadedRows = buildRows(data.shopify_categories || [], data.mappings || [])
                setCategories(cats)
                setRows(loadedRows)
                setSavedMappings(JSON.stringify(toMappings(loadedRows)))
                setWithoutCategory(data.listings_without_category || 0)
            })
            .catch((error) => {
                console.error("Failed to load category mapping:", error)
                toast.error(error.message || "Failed to load category mapping")
            })
            .finally(() => setLoading(false))
    }, [open])

    const categoryOptions = categories.map((cat) => ({ value: cat.id, label: cat.name.toUpperCase() }))
    const hasUnsavedChanges = JSON.stringify(toMappings(rows)) !== savedMappings
    const hasSavedMappings = savedMappings !== "[]"

    const updateRow = (index, changes) => {
        setRows((current) => current.map((row, i) => (i === index ? { ...row, ...changes } : row)))
    }

    const subCategoryOptions = (categoryId) =>
        (categories.find((cat) => cat.id === categoryId)?.subCategories || [])
            .map((subCat) => ({ value: subCat, label: subCat.toUpperCase() }))

    const runPendingAction = async (overwrite) => {
        const action = pendingAction
        setPendingAction(null)
        setRunningAction(action)
        try {
            if (action === "save") {
                const result = await saveShopifyCategoryMappings(toMappings(rows), overwrite)
                toast.success(`Category mapping saved. ${describeResult(result)}`)
            } else {
                const result = await applyShopifyCategoryMappings(overwrite)
                toast.success(`Category mapping applied. ${describeResult(result)}`)
            }
            onSaved?.()
        } catch (error) {
            console.error(`Failed to ${action} category mapping:`, error)
            toast.error(error.message || `Failed to ${action} category mapping`)
        } finally {
            setRunningAction(null)
        }
    }

    if (!open) return null

    return (
        <div className="fixed inset-0 z-[10000] w-screen overflow-y-auto">
            {/* No backdrop-blur: it breaks native <select> clicks in Chromium
                (see DialogEditShopifyCategory). */}
            <div
                className="fixed inset-0 bg-[#000] bg-opacity-75"
                onClick={onClose}
            />

            <div className="relative flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-[860px] border-[1px] border-gray bg-[#141414] p-6">
                    <button onClick={onClose} className="absolute right-4 top-4">
                        <img src="/assets/icons/icon-close-white.svg" alt="close" />
                    </button>

                    <h2 className="text-white text-xl mb-1">Category Mapping</h2>
                    <p className="text-sm text-[#717171] mb-6">
                        Choose a CSE category for each Shopify category used by your listings. Newly synced
                        listings with no category are categorized automatically.
                    </p>

                    {loading ? (
                        <div className="text-white text-center py-8">Loading categories from Shopify...</div>
                    ) : rows.length === 0 ? (
                        <div className="text-white text-center py-8">
                            None of your Shopify listings have a category yet. Set a category on your products in
                            Shopify and they will show up here.
                        </div>
                    ) : (
                        <div className="w-full overflow-auto max-h-[60vh]">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-[#2E2E2E] border-[1px] border-gray text-sm text-[#717171]">
                                        <th className="py-3 pl-3 text-left">Shopify Category</th>
                                        <th className="py-3 text-left">CSE Category</th>
                                        <th className="py-3 text-left">CSE Sub-Category</th>
                                    </tr>
                                </thead>
                                <tbody className="text-white text-sm">
                                    {rows.map((row, index) => (
                                        <tr key={row.shopify_category_id} className="border-[1px] border-gray">
                                            <td className="py-2 pl-3 pr-6 min-w-[220px]">
                                                <div className="uppercase">{row.name}</div>
                                                <div className="text-xs text-[#717171]">{row.full_name}</div>
                                                <div className="text-xs text-[#717171]">
                                                    {row.count} listing{row.count === 1 ? "" : "s"}
                                                </div>
                                            </td>
                                            <td className="pr-3 min-w-[200px]">
                                                <div className="border-[1px] border-gray">
                                                    <Dropdown
                                                        options={categoryOptions}
                                                        value={row.category}
                                                        placeholder="Not mapped"
                                                        allowClear
                                                        onChange={(e) => updateRow(index, { category: e.target.value, sub_category: "" })}
                                                    />
                                                </div>
                                            </td>
                                            <td className="pr-3 min-w-[200px]">
                                                <div className="border-[1px] border-gray">
                                                    <Dropdown
                                                        options={subCategoryOptions(row.category)}
                                                        value={row.sub_category}
                                                        placeholder="None"
                                                        allowClear
                                                        onChange={(e) => updateRow(index, { sub_category: e.target.value })}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && withoutCategory > 0 && (
                        <p className="text-xs text-[#717171] mt-4">
                            {withoutCategory} listing{withoutCategory === 1 ? " has" : "s have"} no category in Shopify
                            and can only be categorized individually.
                        </p>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 mt-8">
                        <button
                            className="solo-button flex-1 text-white border-white border-[1px] bg-primary h-[45px] disabled:opacity-60"
                            onClick={() => setPendingAction("save")}
                            disabled={busy || loading || !hasUnsavedChanges}
                        >
                            {runningAction === "save" ? "Saving..." : "Save Mapping"}
                        </button>
                        <button
                            className="solo-button flex-1 text-white border-gray border-[1px] h-[45px] disabled:opacity-60"
                            onClick={() => setPendingAction("apply")}
                            disabled={busy || loading || hasUnsavedChanges || !hasSavedMappings}
                            title={hasUnsavedChanges ? "Save your changes first" : ""}
                        >
                            {runningAction === "apply" ? "Applying..." : "Apply Mapping"}
                        </button>
                    </div>

                    {pendingAction && (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#000] bg-opacity-80 p-6">
                            <div className="w-full max-w-[440px] border-[1px] border-gray bg-[#141414] p-6">
                                <h3 className="text-white text-lg mb-2">Change listings that already have a category?</h3>
                                <p className="text-sm text-[#717171] mb-6">
                                    Yes updates every listing whose Shopify category is mapped, replacing categories
                                    already assigned. No only categorizes listings that don't have a category yet.
                                </p>
                                <div className="flex flex-col gap-3">
                                    <button
                                        className="solo-button w-full text-white border-white border-[1px] bg-primary h-[45px]"
                                        onClick={() => runPendingAction(true)}
                                    >
                                        Yes, update all listings
                                    </button>
                                    <button
                                        className="solo-button w-full text-white border-white border-[1px] h-[45px]"
                                        onClick={() => runPendingAction(false)}
                                    >
                                        No, only uncategorized listings
                                    </button>
                                    <button
                                        className="w-full text-[#717171] h-[40px] text-sm"
                                        onClick={() => setPendingAction(null)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
